import classNames from 'classnames';
import { Tooltip } from '@crazyegginc/hatch';

import { useAuthContext, usePermissions } from '/src/hooks';

import { FEATURES } from '/src/features/_global/constants';

import { ReactComponent as TeamIcon } from '@crazyegginc/hatch/dist/images/icon-team-outline.svg';
import { AppLink } from '/src/features/_global/components/AppLink';

export function SharedAccountRow({ account, onClick = null }) {
  const { currentUser, currentAccount } = useAuthContext();

  const permissions = usePermissions();

  if (!account) {
    return null;
  }

  const sites = account?.sites ?? [];

  const hasSites = sites.length > 0;
  const displaySites = sites.slice(0, 2);
  const hasMoreSites = sites.length > 2;
  const remainingSites = sites.length - 2;

  const canVisitTeamAndSharing =
    permissions.can('navigate', FEATURES.TEAM).allowed ||
    permissions.can('navigate', FEATURES.SHARING).allowed ||
    permissions.can('navigate', FEATURES.AUDIT_LOG).allowed;
  const canManageAccount = permissions.can('navigate', FEATURES.BILLING).allowed;
  const canVisitBilling = permissions.can('navigate', FEATURES.BILLING).allowed;

  const isActiveAccount = account.isCurrent;
  const isSwitchable = account.id !== currentAccount.id;

  function handleActivate() {
    onClick?.();
  }

  function handleKeyDown(event) {
    if (!isSwitchable) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  }

  return (
    <div
      onClick={isSwitchable ? handleActivate : undefined}
      onKeyDown={isSwitchable ? handleKeyDown : undefined}
      tabIndex={isSwitchable ? 0 : undefined}
      role={isSwitchable ? 'button' : undefined}
      className={classNames(
        'box-border flex min-h-[58px] w-full items-center p-3 outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dodger-blue-500',
        {
          'border-t border-dashed border-mystic-500': currentUser.accounts.length > 1,
          'bg-zumthor-500': account.id === currentAccount.id,
          'hover:bg-alice-blue-500': account.id !== currentAccount.id,
          'cursor-pointer': isSwitchable,
        },
      )}
    >
      <div>
        <Tooltip
          placement="bottom"
          tooltipContent={
            <div className="flex flex-col items-start">
              <span className="font-sm text-white">Shared with you by</span>
              <a href={`mailto:${account.ownerEmail}`}>{account.ownerEmail}</a>
            </div>
          }
        >
          {account.ownerAvatarUrl ? (
            <img
              className="h-[30px] w-[30px] rounded-full fill-current text-dodger-blue-300"
              src={account.ownerAvatarUrl}
              alt="Shared account avatar"
            />
          ) : (
            <TeamIcon className="h-[30px] w-[30px] fill-current text-dodger-blue-300" alt="Shared account avatar" />
          )}
        </Tooltip>
      </div>
      <div className="ml-2 flex flex-col items-start">
        <div className="flex flex-col items-start" data-testid="account_row_sites">
          {hasSites ? (
            <>
              {displaySites.map((site, idx) => (
                <span className="text-body-4 text-black-pearl-500" key={`Account-${account.id}_Site${idx}`}>
                  {site.name}
                </span>
              ))}
              {hasMoreSites && <span className="text-body-4 italic text-[#7786A0]">&amp; {remainingSites} more</span>}
            </>
          ) : (
            <span className="text-body-4">{account.name || account.ownerName}</span>
          )}
        </div>

        {isActiveAccount && (
          <div className="flex flex-col items-start">
            <AppLink to="/account/profile" className="text-link mb-0.5 text-xs">
              {canManageAccount ? 'Manage account' : 'View profile'}
            </AppLink>
            {canVisitTeamAndSharing ? (
              <AppLink to="/account/team" className="text-link mb-0.5">
                Manage team
              </AppLink>
            ) : null}
            {canVisitBilling ? (
              <AppLink to="/account/billing" className="text-link">
                Billing details
              </AppLink>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
